import { faSearch } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { observer } from "mobx-react";
import {
  ChangeEvent,
  ChangeEventHandler,
  useCallback,
  useEffect,
  useState,
} from "react";
import { FormattedMessage } from "react-intl";
import userTypeEnum from "../../constants/enums/user-type-enum";
import { useStore } from "../../contexts/store-provider";
import {
  getHeadquarterIdFromLocalStorage,
  getStoreIdFromLocalStorage,
  getUserTypeFromLocalStorage,
} from "../../helpers/localstorage-helper";
import FormLoader from "../../shared-components/FormLoader/FormLoader";
import DropdownInput from "../../shared-components/input/DropdownInput";
import LanguageSlider from "../../shared-components/language-slider/language-slider";
import { formatMessage } from "../../translations/format-message";
import { Field, Form, Formik } from "formik";
import _ from "lodash";
import { Button } from "react-bootstrap";
import FormikFormSelect from "../../shared-components/FormikFormHandlers/FormikFormSelect";
import './totalling-management.css';
import moment from "moment";
import { DATE_FORMAT } from "../../constants/constants";
import CustomDateInput from "../../shared-components/input/custom-date-input";
import Grid, { getColumns } from "../../shared-components/Grid/Grid";
import { ISorting } from "../../models/ISorting";
import { ColumnType } from "../../constants/enums/grid-column-type-enum";
import { CellContext } from "@tanstack/react-table";
import { showAmountWithFormatting } from "../../helpers/common-component-helper";
import { IStaffSalesDetailList } from "../../models/response/ITotallingManagementResponse";
import { staffTotallingFilterSchema } from "../../shared-components/FormikFormHandlers/FormikValidationSchema";
import FormikDateFieldMUI from "../../shared-components/FormikFormHandlers/FormikDateFieldMUI";

const TotallingManagementStaffSales = () => {
  const { totallingManagementStore, masterManagementStore } = useStore();
  const {
    setStaffTotallingFilterDetail,
    resetStaffTotallingFilterDetail,
    staffTotallingFilter,
    GetStaffSalesDetailService,
    resetStaffSalesDetailState,
    getStaffSalesDetail,
    staffSalesDetailState
  } = totallingManagementStore;
  const { GetAllStoreOptionService, getAllStoreOptionsAll, GetAllStaffOptionServiceByStoreId, getAllStaffOptionsWithSelectStaff, staffOptionsState } =
    masterManagementStore;
  const userType = getUserTypeFromLocalStorage();
  let storeId = getStoreIdFromLocalStorage() || 0;
  let headquarterId = getHeadquarterIdFromLocalStorage() || 0;

  const [storeIdSearch, setStoreIdSearch] = useState<number>(
    userType === userTypeEnum[userTypeEnum.Store] ? storeId : -1
  );

  const [sorting, setSorting] = useState<ISorting>({
    column: "styaff_name",
    orderBy: "asc",
  });

  const handleSortingChange = (accessor: string) => {
    let sortOrder = sorting.orderBy;
    if (accessor === sorting.column) {
      sortOrder = sorting.orderBy === "asc" ? "desc" : "asc";
    }
    setSorting({ column: accessor, orderBy: sortOrder });
  };

  const handleStoreIdSearch: ChangeEventHandler<HTMLSelectElement> =
    useCallback((event: ChangeEvent<HTMLSelectElement>) => {
      setStoreIdSearch(event.target.value as unknown as number);
    }, []);

  const callGetStaffSalesDetailService = useCallback(() => {
    GetStaffSalesDetailService(
      Number(storeIdSearch),
      headquarterId,
      staffTotallingFilter.StaffId,
      staffTotallingFilter.StartDate,
      staffTotallingFilter.EndDate
    );
  }, [
    GetStaffSalesDetailService,
    storeIdSearch,
    headquarterId,
    sorting
  ]);

  useEffect(() => {
    if (userType === userTypeEnum[userTypeEnum.HeadQuarter])
      GetAllStoreOptionService();
    return () => {
      resetStaffTotallingFilterDetail();
      resetStaffSalesDetailState();
    };
  }, []);

  useEffect(() => {
    callGetStaffSalesDetailService();
  }, [callGetStaffSalesDetailService]);

  useEffect(() => {
      if(staffTotallingFilter.isFilterChanged){
        GetStaffSalesDetailService(
          Number(storeIdSearch),
          headquarterId,
          staffTotallingFilter.StaffId,
          staffTotallingFilter.StartDate,
          staffTotallingFilter.EndDate
        );
        setStaffTotallingFilterDetail({...staffTotallingFilter, isFilterChanged: false})
      }
    }, [staffTotallingFilter.isFilterChanged]);

  
  useEffect(() => {
    GetAllStaffOptionServiceByStoreId(storeIdSearch, false, userType === userTypeEnum[userTypeEnum.HeadQuarter] ? headquarterId : null);
  }, [storeIdSearch]);

  const columArray: any = [
      {
        columnType: ColumnType.Hidden,
        accessorKey: "Id",
      },
      {
        columnType: ColumnType.TextHeader,
        accessorKey: "StaffName",
        displayName: "staff_name",
        sortingName: "staff_name",
      },
      {
        columnType: ColumnType.JsxElement,
        accessorKey: "TotalSales",
        displayName: "total_sales",
        jsxElementFormatter: (info: CellContext<any, any>) => {
          let cell = info.getValue();
          return cell !== "" ? showAmountWithFormatting(cell) : "";
        },
      },
      {
        columnType: ColumnType.JsxElement,
        accessorKey: "TreatmentSales",
        displayName: "treatment_sales",
        jsxElementFormatter: (info: CellContext<any, any>) => {
          let cell = info.getValue();
          return cell !== "" ? showAmountWithFormatting(cell) : "";
        },
      },
      {
        columnType: ColumnType.JsxElement,
        accessorKey: "ProductSales",
        displayName: "product_sales",
        jsxElementFormatter: (info: CellContext<any, any>) => {
          let cell = info.getValue();
          return cell !== "" ? showAmountWithFormatting(cell) : "";
        },
      },
      {
        columnType: ColumnType.TextHeader,
        accessorKey: "TotalNumberOfCustomer",
        displayName: "total_number_of_customers",
      },
      {
        columnType: ColumnType.JsxElement,
        accessorKey: "UnitPrice",
        displayName: "unit_price",
        jsxElementFormatter: (info: CellContext<any, any>) => {
          let cell = info.getValue();
          return cell !== "" ? showAmountWithFormatting(cell) : "";
        },
      },
      {
        columnType: ColumnType.JsxElement,
        accessorKey: "NominatedCustomer",
        displayName: "nominated_customers",
        jsxElementFormatter: (info: CellContext<any, any>) => {
          const row = info.row.original;
          return (
            <>
              <span>{row.NominatedCustomer}</span>
              <span className="text-muted ms-1">({row.NominatedCustomerPercentage} %)</span>
            </>
          );
        },
      },
    ];
    const columns = getColumns<IStaffSalesDetailList, number>(
      columArray,
      sorting,
      handleSortingChange
    );

  return (
    <>
      {(staffSalesDetailState.inProgress ||
        staffOptionsState.inProgress ||
        masterManagementStore.inProgress) && (
        <FormLoader
          loading={
            staffSalesDetailState.inProgress ||
            staffOptionsState.inProgress ||
            masterManagementStore.inProgress
          }
        />
      )}
      <div className="icon-nav navbar-fixed-top device-nav-bar userTopNav">
        <div className="row align-items-center">
          <div className="col-6">
            <div className="page_title_wrap">
              <h1 className="page_title">
                <FormattedMessage id="staff_aggregate_sales" />
              </h1>
              <ol className="breadcrumb">
                <li className="breadcrumb-item">
                  <FormattedMessage id="aggregate" />
                </li>
                <li className="breadcrumb-item active" aria-current="page">
                  <FormattedMessage id="staff_sales" />
                </li>
              </ol>
            </div>
          </div>
          <div className="col-6">
            <ul className="add-list">
              <li>
                <LanguageSlider />
              </li>
              <li>
                {userType !== userTypeEnum[userTypeEnum.Store] && (
                  <>
                    {storeIdSearch && (
                      <div className="input-group">
                        <div className="input-group-text">
                          <span role="button" onClick={() => {}}>
                            <FontAwesomeIcon icon={faSearch} />
                          </span>
                        </div>
                        <DropdownInput
                          className="form-control"
                          placeholder={formatMessage("search_by_store_name")}
                          value={storeIdSearch}
                          onChange={(event)=>{
                            setStaffTotallingFilterDetail({...staffTotallingFilter, StaffId: -1});
                            handleStoreIdSearch(event)
                          }}
                          options={getAllStoreOptionsAll.map((option) => ({
                            label: option.value,
                            value: option.id,
                          }))}
                        />
                      </div>
                    )}
                  </>
                )}
              </li>
            </ul>
          </div>
        </div>
      </div>
      <div className="mt-4 mb-2">
        <Formik
          initialValues={_.omit(staffTotallingFilter, ["isFilterChanged", "isFilterActive"])}
          enableReinitialize={true}
          validationSchema={staffTotallingFilterSchema}
          validateOnBlur={true}
          validateOnChange={true}
          onSubmit={async (values) => {
            let tempStaffId = typeof values.StaffId === "string" ? parseInt(values.StaffId) : values.StaffId;
            let tempValues = {
              StartDate: values.StartDate,
              EndDate: values.EndDate,
              StaffId: tempStaffId 
            };
            setStaffTotallingFilterDetail({
              ...tempValues,
              isFilterActive: true,
              isFilterChanged: true,
              showClear: _.isEqual({
                  StartDate: moment(new Date()).format(DATE_FORMAT),
                  EndDate: moment(new Date()).add(6, "days").format(DATE_FORMAT),
                  StaffId: -1,
                  showClear: false
                }, values) ? false : true
            });
          }}
        >
          {({ values, setFieldValue }) => {
            return (
              <Form className="user-form p-0 ">
                <div className="row align-items-center">
                   <div className="col-md-3 form-group">
                      <Field
                        name="StartDate"
                        as={FormikDateFieldMUI}
                        disableFuture={true}
                      />
                    </div>
                    <div className="col-md-3 form-group">
                      <Field
                        name="EndDate"
                        as={FormikDateFieldMUI}
                        disableFuture={true}
                      />
                    </div>
                  <div className="col-md-3 form-group">
                    <Field
                      name="StaffId"
                      getData={false}
                      options={getAllStaffOptionsWithSelectStaff}
                      as={FormikFormSelect}
                    />
                  </div>
                  <div className="col-md-3 form-group text-start">
                    <Button
                      type="submit"
                      className="btn btn-primary"
                    >
                      <FormattedMessage id="apply" />
                    </Button>
                    {staffTotallingFilter.isFilterActive && staffTotallingFilter.showClear && (
                      <Button
                        type="button"
                        disabled={staffSalesDetailState.inProgress}
                        className="ms-2"
                        variant="btn btn-secondary"
                        onClick={()=>{
                          setStaffTotallingFilterDetail({
                            StartDate: moment(new Date()).format(DATE_FORMAT),
                            EndDate: moment(new Date()).add(6, "days").format(DATE_FORMAT),
                            StaffId: -1,
                            isFilterActive: false,
                            isFilterChanged: true,
                            showClear: false,
                          })
                        }}
                      >
                        <FormattedMessage id="clear" />
                      </Button>
                    )}
                  </div>
                </div>
              </Form>
            );
          }}
        </Formik>
      </div>
      <div className=" pb-4">
        <div>
          <div className="card changingPanel">
            <div className="tableDesign row-eq-height tableDevices">
              <div
                className="table-responsive"
                data-testid="table"
              >
                {getStaffSalesDetail && (
                  <Grid
                    data={getStaffSalesDetail}
                    columns={columns}
                    isInProgress={staffSalesDetailState.inProgress}
                    hiddenColumns={["Id"]}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default observer(TotallingManagementStaffSales);
